import { CSSProperties, useLayoutEffect, useState } from 'react';
import { Color } from '@vivli/shared/theme';

const uploadingContainerStyle: CSSProperties = {
    color: Color.WHITE,
    width: 100,
    height: 30,
    marginRight: 10,
    backgroundColor: Color.VIVLI_LIGHT_BLUE,
    position: 'relative'
};

const uploadingStyle = (progressPercent: number): CSSProperties => ({
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'inline-block',
    width: `${progressPercent}%`,
    height: 30,
    transition: 'width 0.4s ease-in-out',
    backgroundColor: Color.VIVLI_BLUE
});

const uploadingTextStyle: CSSProperties = {
    position: 'absolute',
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
};

interface UploadProgressBarComponentProps {
    uploaderId?: string;
    subscribeFileProgress?: (id: string, cb: (pct: number) => void) => () => void;
}

// Self-contained progress bar. It subscribes to per-tick upload progress for a single
// uploaderId so that progress updates re-render only this small component, leaving the
// surrounding controls cell (and the Cancel button) untouched. This keeps the Cancel
// button's DOM node stable during an upload so a single click registers in every browser
// (Firefox dropped clicks when the cell was recreated mid-press). (DEV-479)
export const UploadProgressBarComponent = ({ uploaderId, subscribeFileProgress }: UploadProgressBarComponentProps) => {
    const [percentUploaded, setPercentUploaded] = useState(0);

    // Must be a LAYOUT effect, not a plain effect. This component lives in an ag-grid cell renderer
    // that has no refresh(), so the cell — and therefore this component — is destroyed and recreated
    // whenever the row's data object changes identity, which resets the state above to 0. Subscribing
    // after paint meant every one of those remounts painted a 0% bar first and then animated up to the
    // real percentage through the 0.4s width transition, so a single upload appeared to run 0→100
    // several times. subscribeFileProgress invokes the callback synchronously with the last-known
    // value, so doing it before paint makes the first painted frame the correct width. (DEV-711)
    useLayoutEffect(() => {
        if (!uploaderId || !subscribeFileProgress) {
            return;
        }
        return subscribeFileProgress(uploaderId, setPercentUploaded);
    }, [uploaderId, subscribeFileProgress]);

    return (
        <div style={uploadingContainerStyle}>
            <div style={uploadingStyle(percentUploaded)} />
            <div style={uploadingTextStyle}>Uploading</div>
        </div>
    );
};
